import React, {
  MouseEvent,
  ReactElement, ReactNode, useEffect, useState,
} from 'react';
import {
  Container,
  Item,
  Label,
  IconWrapper,
  NoIcon,
  EmptyButton,
} from './styles';
import { ReactComponent as PowerIcon } from '../../../assets/img/icons/Power.svg';
import { ReactComponent as CoolingIcon } from '../../../assets/img/icons/Cooling.svg';
import { ReactComponent as VentilationIcon } from '../../../assets/img/icons/Ventilation.svg';
import { ReactComponent as FreeModeIcon } from '../../../assets/img/icons/FreeMode.svg';
import { ReactComponent as WifiOnIcon } from '../../../assets/img/icons/WifiOn.svg';
import { ReactComponent as WifiOffIcon } from '../../../assets/img/icons/WifiOff.svg';
import { t } from 'i18next';
import Skeleton from 'react-loading-skeleton';
import { HealthMachineInfoModal } from '~/components/ModalsRemoteControl/HealthMachineInfo';
import { HealthStatusIndicator } from '~/components/HealthStatusIndicator';
import { HealthStatus } from '../MachineProfile/types';

export type StatusType = 'on' | 'off' | 'freeMode';
export type OperationType = 'cooling' | 'ventilating';

interface StatusProps {
  isConnected: boolean;
  isListScreen?: boolean;
  operation?: OperationType;
  status?: StatusType;
  isLoading?: boolean;
  healthStatus?: HealthStatus;
  /** Saúde no Controle da unidade; ausente é tratado como visível. */
  healthVisible?: boolean;
}

interface StatusItemProps {
  width: number;
  children: ReactNode;
  isLoading?: boolean;
}

const HealthIndicator: React.FC<{status?: HealthStatus }> = ({
  status,
}) => {
  const [openStatusInfoModal, setOpenStatusInfoModal] = useState(false);

  function onCloseModal(): void {
    setOpenStatusInfoModal(false);
  }

  function onClickIndicator(event: MouseEvent<HTMLButtonElement>): void {
    event.stopPropagation();
    event.preventDefault();
    setOpenStatusInfoModal(true);
  }

  if (!status) {
    return (
      <NoIcon>-</NoIcon>
    );
  }

  return (
    <>
      <HealthMachineInfoModal
        healthStatus={status}
        isOpen={openStatusInfoModal}
        onClose={onCloseModal}
      />
      <EmptyButton
        type="button"
        onClick={onClickIndicator}
        data-testid="health-status-indicator-button"
      >
        <HealthStatusIndicator status={status} />
      </EmptyButton>
    </>
  );
};

const StatusItem: React.FC<StatusItemProps> = ({
  width,
  isLoading,
  children,
}) => {
  if (isLoading) {
    return (
      <Item>
        <Skeleton width={width} height={12} style={{ marginBottom: 8 }} />
        <IconWrapper removeMargin>
          <Skeleton width={24} height={24} borderRadius={0} />
        </IconWrapper>
      </Item>
    );
  }

  return (
    <Item>{children}</Item>
  );
};

export const StatusCard: React.FC<StatusProps> = ({
  isConnected,
  isListScreen = false,
  operation,
  status,
  isLoading,
  healthStatus,
  healthVisible = true,
}) => {
  function getStatusLabel(status?: StatusType) {
    if (status === 'on') return t('ligada');
    if (status === 'off') return t('desligada');
    if (status === 'freeMode') return t('modoLivreAtivado');
    return '';
  }

  function getStatusColor(status: StatusType) {
    if (status === 'on') {
      return '#39B719';
    }

    if (status === 'off') {
      return '#DD1818';
    }

    return verifyColorByScreen();
  }

  function renderStatusIcon(): ReactElement {
    if (!status) return <NoIcon>-</NoIcon>;

    const color = getStatusColor(status);

    if (status === 'freeMode') {
      return <FreeModeIcon style={{ color }} />;
    }

    return <PowerIcon style={{ color }} />;
  }

  function verifyColorByScreen() {
    return isListScreen ? 'black' : 'white';
  }

  function getOperationColor(operation: OperationType) {
    if (operation === 'cooling' && isListScreen) {
      return '#3180C6';
    }

    if (operation === 'ventilating' && isListScreen) {
      return 'black';
    }

    return 'white';
  }

  function getOperationLabel(): string {
    if (operation === 'cooling') return t('resfriando');
    if (operation === 'ventilating') return t('ventilando');
    return '';
  }

  function renderOperationIcon(): ReactElement {
    if (operation) {
      const color = getOperationColor(operation);
      if (operation === 'cooling') {
        return <CoolingIcon style={{ color }} />;
      }

      if (operation === 'ventilating') {
        return <VentilationIcon style={{ color }} />;
      }
    }

    return <NoIcon>-</NoIcon>;
  }

  function getHealthStatusLabel(healthStatus?: HealthStatus): string {
    switch (healthStatus) {
      case HealthStatus.NO_ALERT: return t('semAlerta');
      case HealthStatus.ALERT: return t('emAlerta');
      case HealthStatus.INOPERATIVE: return t('inoperante');
      default: return '';
    }
  }

  const [width, setWidth] = useState<number>(100);
  useEffect(() => {
    const updateWidth = () => {
      const windowWidth = window.innerWidth;

      if (windowWidth < 480) {
        setWidth(75);
      } else if (windowWidth < 768) {
        setWidth(70);
      } else if (windowWidth < 1280) {
        setWidth(60);
      } else {
        setWidth(85);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const showHealth = healthVisible && (isListScreen || (isConnected && !!healthStatus));
  const noWrapLabels = !healthVisible;

  return (
    <Container>
      <StatusItem isLoading={isLoading} width={width}>
        <Label isListScreen={isListScreen} noWrap={noWrapLabels}>
          {isConnected ? t('conectada') : t('desconectada')}
        </Label>
        <IconWrapper>
          {isConnected
            ? <WifiOnIcon style={{ color: verifyColorByScreen() }} />
            : <WifiOffIcon style={{ color: verifyColorByScreen() }} />}
        </IconWrapper>
      </StatusItem>
      {showHealth ? (
        <StatusItem isLoading={isLoading} width={width}>
          <Label isListScreen={isListScreen}>
            {getHealthStatusLabel(isConnected ? healthStatus : undefined)}
          </Label>
          <IconWrapper>
            <HealthIndicator status={isConnected ? healthStatus : undefined} />
          </IconWrapper>
        </StatusItem>
      ) : null}
      {(isListScreen || operation) && (
        <StatusItem isLoading={isLoading} width={width}>
          <Label isListScreen={isListScreen} noWrap={noWrapLabels}>
            {getOperationLabel()}
          </Label>
          <IconWrapper>
            {renderOperationIcon()}
          </IconWrapper>
        </StatusItem>
      )}
      {(isListScreen || status) && (
        <StatusItem isLoading={isLoading} width={width}>
          <Label isListScreen={isListScreen} noWrap={noWrapLabels}>
            {getStatusLabel(status)}
          </Label>
          <IconWrapper>
            {renderStatusIcon()}
          </IconWrapper>
        </StatusItem>
      )}
    </Container>
  );
};
